// ============================================================
//  MASAMADREAM — Circular Dial (SVG)
//  Fase 2: 7-segment clock, progress arc, mascot center, phrases
// ============================================================

import { PROCESSES, PHRASES } from './data.js';

const NS = 'http://www.w3.org/2000/svg';

// SVG geometry constants
const CX         = 150;   // center x
const CY         = 150;   // center y
const R_OUTER    = 143;   // outer edge of day-ring
const R_INNER    = 102;   // inner edge of day-ring
const R_LABEL    = 122;   // midpoint of ring (day text)
const R_PROGRESS = 108;   // progress arc radius
const R_CENTER   = 96;    // center circle radius

// Logo URL (resolved by Parcel at build time)
const LOGO_URL = new URL('../assets/logo.png', import.meta.url);

// ── Helpers ───────────────────────────────────────────────────
function svgEl(tag, attrs = {}) {
  const node = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) {
    node.setAttribute(k, String(v));
  }
  return node;
}

// 0° = 12 o'clock, positive = clockwise
function toXY(r, deg) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [CX + r * Math.cos(rad), CY + r * Math.sin(rad)];
}

// SVG arc path between two angles at radius r
function arcPath(r, a1, a2) {
  const [x1, y1] = toXY(r, a1);
  const [x2, y2] = toXY(r, a2);
  const large     = a2 - a1 > 180 ? 1 : 0;
  return `M ${x1.toFixed(3)} ${y1.toFixed(3)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(3)} ${y2.toFixed(3)}`;
}

// Donut sector path (outer arc CW → inner arc CCW)
function sectorPath(a1, a2) {
  const [ox1, oy1] = toXY(R_OUTER, a1);
  const [ox2, oy2] = toXY(R_OUTER, a2);
  const [ix1, iy1] = toXY(R_INNER, a1);
  const [ix2, iy2] = toXY(R_INNER, a2);
  const large = a2 - a1 > 180 ? 1 : 0;
  return [
    `M ${ox1.toFixed(3)} ${oy1.toFixed(3)}`,
    `A ${R_OUTER} ${R_OUTER} 0 ${large} 1 ${ox2.toFixed(3)} ${oy2.toFixed(3)}`,
    `L ${ix2.toFixed(3)} ${iy2.toFixed(3)}`,
    `A ${R_INNER} ${R_INNER} 0 ${large} 0 ${ix1.toFixed(3)} ${iy1.toFixed(3)}`,
    'Z',
  ].join(' ');
}

// ── Main factory ───────────────────────────────────────────────
export function createDial(process) {
  const pData      = PROCESSES[process.type];
  const totalDays  = pData.totalDays;
  const curDay     = Math.max(1, Math.min(process.currentDay, totalDays));
  const segAngle   = 360 / totalDays;
  const progAngle  = ((curDay - 1) / totalDays) * 360; // degrees filled

  const svg = svgEl('svg', {
    viewBox: '0 0 300 300',
    width:   '300',
    height:  '300',
    role:    'img',
    'aria-label': `Progreso: Día ${curDay} de ${totalDays}`,
    class:   'mm-dial-svg',
  });

  // ── Defs ────────────────────────────────────────────────────
  const defs = svgEl('defs');

  // Progress gradient
  const grad = svgEl('linearGradient', {
    id: 'mm-pg', gradientUnits: 'userSpaceOnUse',
    x1: CX, y1: CY - R_PROGRESS, x2: CX + R_PROGRESS, y2: CY + R_PROGRESS / 2,
  });
  const s1 = svgEl('stop', { offset: '0%' });  s1.style.cssText = 'stop-color:#90CAF9';
  const s2 = svgEl('stop', { offset: '100%' }); s2.style.cssText = 'stop-color:#1565C0';
  grad.appendChild(s1);
  grad.appendChild(s2);
  defs.appendChild(grad);

  // Center drop shadow
  const filt = svgEl('filter', { id: 'mm-shadow', x: '-15%', y: '-15%', width: '130%', height: '130%' });
  filt.appendChild(svgEl('feDropShadow', {
    dx: '0', dy: '3', stdDeviation: '5', 'flood-color': 'rgba(109,76,65,0.18)',
  }));
  defs.appendChild(filt);

  // Clip for center logo (circle)
  const clip = svgEl('clipPath', { id: 'mm-logo-clip' });
  clip.appendChild(svgEl('circle', { cx: CX, cy: CY - 6, r: '42' }));
  defs.appendChild(clip);

  svg.appendChild(defs);

  // ── Ring background ──────────────────────────────────────────
  const ringMid = (R_OUTER + R_INNER) / 2;
  const ringW   = R_OUTER - R_INNER;
  svg.appendChild(svgEl('circle', {
    cx: CX, cy: CY, r: ringMid,
    fill: 'none', stroke: '#F0E8D8', 'stroke-width': ringW,
  }));

  // Ring borders
  svg.appendChild(svgEl('circle', { cx: CX, cy: CY, r: R_OUTER, fill: 'none', stroke: '#D0C0A8', 'stroke-width': '2' }));
  svg.appendChild(svgEl('circle', { cx: CX, cy: CY, r: R_INNER, fill: 'none', stroke: '#D0C0A8', 'stroke-width': '1' }));

  // ── Completed day sectors (green tint) ───────────────────────
  for (let i = 0; i < curDay - 1; i++) {
    const a1 = i * segAngle;
    const a2 = (i + 1) * segAngle;
    svg.appendChild(svgEl('path', {
      d: sectorPath(a1, a2),
      fill: 'rgba(67,160,71,0.14)',
    }));
  }

  // ── Active day sector (blue tint) ────────────────────────────
  {
    const a1 = (curDay - 1) * segAngle;
    const a2 = curDay * segAngle;
    svg.appendChild(svgEl('path', {
      d: sectorPath(a1, a2),
      fill: 'rgba(33,150,243,0.13)',
    }));
  }

  // ── Segment dividers ─────────────────────────────────────────
  for (let i = 0; i < totalDays; i++) {
    const angle    = i * segAngle;
    const [x1, y1] = toXY(R_INNER, angle);
    const [x2, y2] = toXY(R_OUTER, angle);
    svg.appendChild(svgEl('line', {
      x1: x1.toFixed(3), y1: y1.toFixed(3),
      x2: x2.toFixed(3), y2: y2.toFixed(3),
      stroke: '#C8B090', 'stroke-width': '1.5',
    }));
  }

  // ── Day labels ───────────────────────────────────────────────
  for (let i = 0; i < totalDays; i++) {
    const dayNum   = i + 1;
    const midAngle = i * segAngle + segAngle / 2;
    const [lx, ly] = toXY(R_LABEL, midAngle);
    const isDone   = dayNum < curDay;
    const isToday  = dayNum === curDay;

    const fill   = isToday ? '#1565C0' : isDone ? '#2E7D32' : '#6D4C41';
    const weight = isToday || isDone ? '700' : '600';
    const size   = totalDays <= 7 ? '10.5' : '9.5';

    const g = svgEl('g', { transform: `rotate(${midAngle}, ${lx.toFixed(3)}, ${ly.toFixed(3)})` });
    const t = svgEl('text', {
      x: lx.toFixed(3), y: ly.toFixed(3),
      'text-anchor': 'middle',
      'dominant-baseline': 'central',
      fill,
      'font-family': "'Signika Negative', sans-serif",
      'font-size': size,
      'font-weight': weight,
      'letter-spacing': '0.4',
    });
    // Show checkmark emoji for completed days
    t.textContent = isDone ? `✓ DÍA ${dayNum}` : `DÍA ${dayNum}`;
    g.appendChild(t);
    svg.appendChild(g);
  }

  // ── Progress arc ─────────────────────────────────────────────
  if (progAngle > 0.5) {
    const clampedEnd = Math.min(progAngle, 359.9);
    const progEl = svgEl('path', {
      d: arcPath(R_PROGRESS, 0, clampedEnd),
      fill: 'none',
      stroke: 'url(#mm-pg)',
      'stroke-width': '8',
      'stroke-linecap': 'round',
      class: 'mm-dial-progress',
    });
    svg.appendChild(progEl);

    // Leading dot
    const [dx, dy] = toXY(R_PROGRESS, progAngle);
    svg.appendChild(svgEl('circle', {
      cx: dx.toFixed(3), cy: dy.toFixed(3), r: '7',
      fill: '#2196F3',
      stroke: '#FFFFFF',
      'stroke-width': '2.5',
      filter: 'url(#mm-shadow)',
    }));
  }

  // ── Center circle ─────────────────────────────────────────────
  svg.appendChild(svgEl('circle', {
    cx: CX, cy: CY, r: R_CENTER,
    fill: 'rgba(248,245,236,0.94)',
    filter: 'url(#mm-shadow)',
  }));

  // ── Logo image ────────────────────────────────────────────────
  const logoImg = svgEl('image', {
    href: LOGO_URL.href,
    x: CX - 42, y: CY - 50,
    width: '84',
    height: '84',
    preserveAspectRatio: 'xMidYMid meet',
    'clip-path': 'url(#mm-logo-clip)',
  });
  svg.appendChild(logoImg);

  // ── Motivational phrase ──────────────────────────────────────
  const phraseIdx = Math.floor(Date.now() / 30000) % PHRASES.length;
  const phraseEl  = svgEl('text', {
    id: 'mm-dial-phrase',
    x: CX, y: CY + 51,
    'text-anchor': 'middle',
    'dominant-baseline': 'central',
    fill: '#A07860',
    'font-family': "'Signika Negative', sans-serif",
    'font-size': '8.5',
    'font-style': 'italic',
  });
  phraseEl.textContent = PHRASES[phraseIdx];
  svg.appendChild(phraseEl);

  return svg;
}

// ── Phrase rotation (every 8s) ────────────────────────────────
let _phraseTimer = null;

export function startPhraseRotation() {
  stopPhraseRotation();
  let idx = Math.floor(Date.now() / 30000) % PHRASES.length;

  _phraseTimer = setInterval(() => {
    const el = document.getElementById('mm-dial-phrase');
    if (!el) return;
    idx = (idx + 1) % PHRASES.length;
    el.style.opacity = '0';
    setTimeout(() => {
      el.textContent = PHRASES[idx];
      el.style.opacity = '1';
    }, 350);
  }, 8000);
}

export function stopPhraseRotation() {
  if (_phraseTimer !== null) {
    clearInterval(_phraseTimer);
    _phraseTimer = null;
  }
}
